import type { PostgrestError } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  buildEntityWorkspaceSnapshot,
  mapEntityCapacityRow,
  parseEntityLimitError,
} from "@/features/entities/mappers/entityMappers";
import {
  buildEntityRecordPayload,
  buildEntityRelationshipLabel,
  createEntityCardSchema,
  entityRelationshipSchema,
  getStoredRelationshipType,
  updateEntityCardSchema,
} from "@/features/entities/validation/entitySchemas";
import {
  createEntityImageSignedUrl,
  deleteEntityImage,
  uploadEntityImage,
} from "@/features/entities/api/entityStorage";
import { loreCanonStatusLabels } from "@/types/lore";
import {
  createEntityDraft,
  toStoredEntityCardType,
  toStoredRelationshipKind,
  type CreateEntityCardInput,
  type EntityCardType,
  type CreateEntityRelationshipInput,
  type EntityCardRecord,
  type EntityCardTargetOption,
  type EntityItemCategory,
  type EntityLoreOption,
  type EntityRelationshipRecord,
  type EntityRelationshipTargetOption,
  type EntityWorkspaceSnapshot,
  type ProjectEntityCapacitySummary,
  type UpdateEntityCardInput,
  type UpdateEntityRelationshipInput,
} from "@/types/entity";

type EntityCardRow = Database["public"]["Tables"]["entity_cards"]["Row"];
type EntityRelationshipRow = Database["public"]["Tables"]["relationships"]["Row"];
type LoreLookupRow = Pick<
  Database["public"]["Tables"]["lore_entries"]["Row"],
  "canon_status" | "id" | "title" | "type"
>;

export type EntityActionErrorCode =
  | "generic"
  | "missing-config"
  | "access-denied"
  | "not-found"
  | "entity-limit-reached"
  | "relationship-invalid";

interface EntityActionErrorOptions {
  cause?: unknown;
  limitPrompt?: ReturnType<typeof parseEntityLimitError>;
}

export class EntityActionError extends Error {
  readonly cause?: unknown;
  readonly limitPrompt?: ReturnType<typeof parseEntityLimitError>;

  constructor(
    public readonly code: EntityActionErrorCode,
    message?: string,
    options?: EntityActionErrorOptions,
  ) {
    super(message ?? code);
    this.name = "EntityActionError";
    this.cause = options?.cause;
    this.limitPrompt = options?.limitPrompt;
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new EntityActionError(
      "missing-config",
      "Supabase environment variables are not configured.",
    );
  }
}

async function getCurrentUserId() {
  ensureConfigured();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new EntityActionError("access-denied", "No authenticated user available.");
  }

  return user.id;
}

function mapEntityError(error: unknown): EntityActionError {
  if (error instanceof EntityActionError) {
    return error;
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String(error.message)
      : "Entity action failed.";

  const limitPrompt = parseEntityLimitError(message);
  if (limitPrompt) {
    return new EntityActionError("entity-limit-reached", message, {
      cause: error,
      limitPrompt,
    });
  }

  if (
    message === "RELATIONSHIP_SOURCE_INVALID"
    || message === "RELATIONSHIP_TARGET_INVALID"
    || message === "RELATIONSHIP_SCOPE_INVALID"
    || message === "RELATIONSHIP_TYPE_INVALID"
  ) {
    return new EntityActionError("relationship-invalid", message, { cause: error });
  }

  const postgrestError = error as PostgrestError | undefined;
  if (postgrestError?.code === "PGRST116") {
    return new EntityActionError("not-found", "That Entity Card could not be found.", {
      cause: error,
    });
  }

  if (postgrestError?.code === "42501" || message.toLowerCase().includes("row-level security")) {
    return new EntityActionError("access-denied", "You do not have access to that project.", {
      cause: error,
    });
  }

  return new EntityActionError("generic", message, { cause: error });
}

export function formatEntityErrorMessage(error: unknown) {
  const entityError = mapEntityError(error);

  switch (entityError.code) {
    case "missing-config":
      return "entities.messages.missingConfig";
    case "access-denied":
      return "entities.messages.accessDenied";
    case "not-found":
      return "entities.messages.notFound";
    case "entity-limit-reached":
      return "entities.messages.limitReached";
    case "relationship-invalid":
      return "entities.messages.relationshipInvalid";
    default:
      return "entities.messages.saveError";
  }
}

async function getOwnedEntityCardById(cardId: string) {
  ensureConfigured();

  const { data, error } = await supabase.from("entity_cards").select("*").eq("id", cardId).maybeSingle();

  if (error) {
    throw mapEntityError(error);
  }

  return (data as EntityCardRow | null) ?? null;
}

async function resolveEntityImageUrls(rows: EntityCardRow[]) {
  const results = await Promise.all(
    rows.map(async (row) => {
      if (!row.image_path) {
        return [row.id, null] as const;
      }

      try {
        const signedUrl = await createEntityImageSignedUrl(row.image_path);
        return [row.id, signedUrl] as const;
      } catch {
        return [row.id, null] as const;
      }
    }),
  );

  return new Map(results);
}

function isEntityItemCategory(value: unknown): value is EntityItemCategory {
  return value === "weapon"
    || value === "artifact"
    || value === "tool"
    || value === "relic"
    || value === "technology";
}

export function buildImportedEntityDraft(input: {
  displayTitle: string;
  elementData: Record<string, unknown>;
  elementType: "character" | "item";
}): {
  draft: Omit<CreateEntityCardInput, "projectId">;
  type: EntityCardType;
} {
  if (input.elementType === "character") {
    return {
      draft: {
        ...createEntityDraft("character"),
        ageText: typeof input.elementData.ageText === "string" ? input.elementData.ageText : "",
        aliases: Array.isArray(input.elementData.aliases)
          ? input.elementData.aliases.filter((value): value is string => typeof value === "string")
          : [],
        name:
          typeof input.elementData.name === "string" && input.elementData.name.trim().length > 0
            ? input.elementData.name
            : input.displayTitle,
        notes: typeof input.elementData.notes === "string" ? input.elementData.notes : "",
        personalityTraits:
          typeof input.elementData.personalityTraits === "string"
            ? input.elementData.personalityTraits
            : "",
        physicalDescription:
          typeof input.elementData.physicalDescription === "string"
            ? input.elementData.physicalDescription
            : "",
        raceLoreEntryId:
          typeof input.elementData.raceLoreEntryId === "string"
            ? input.elementData.raceLoreEntryId
            : null,
        skillsAndAbilities:
          typeof input.elementData.skillsAndAbilities === "string"
            ? input.elementData.skillsAndAbilities
            : "",
      },
      type: "character",
    };
  }

  return {
    draft: {
      ...createEntityDraft("item-object"),
      currentHolderEntityCardId:
        typeof input.elementData.currentHolderEntityCardId === "string"
          ? input.elementData.currentHolderEntityCardId
          : null,
      itemCategory: isEntityItemCategory(input.elementData.itemCategory)
        ? input.elementData.itemCategory
        : "artifact",
      itemDescription:
        typeof input.elementData.itemDescription === "string"
          ? input.elementData.itemDescription
          : typeof input.elementData.notes === "string"
            ? input.elementData.notes
            : "",
      name:
        typeof input.elementData.name === "string" && input.elementData.name.trim().length > 0
          ? input.elementData.name
          : input.displayTitle,
      origin: typeof input.elementData.origin === "string" ? input.elementData.origin : "",
    },
    type: "item-object",
  };
}

export function buildImportedEntityElementData(
  input: Omit<CreateEntityCardInput, "projectId">,
  baseElementData: Record<string, unknown> = {},
) {
  switch (input.type) {
    case "character":
      return {
        ...baseElementData,
        ageText: input.ageText ?? "",
        aliases: input.aliases ?? [],
        name: input.name,
        notes: input.notes ?? "",
        personalityTraits: input.personalityTraits ?? "",
        physicalDescription: input.physicalDescription ?? "",
        raceLoreEntryId: input.raceLoreEntryId ?? null,
        skillsAndAbilities: input.skillsAndAbilities ?? "",
      };
    case "location":
      return {
        ...baseElementData,
        locationDescription: input.locationDescription ?? "",
        locationNotes: input.locationNotes ?? "",
        name: input.name,
      };
    case "machine":
      return {
        ...baseElementData,
        currentHolderEntityCardId: input.currentHolderEntityCardId ?? null,
        machineCapabilities: input.machineCapabilities ?? "",
        machineDescription: input.machineDescription ?? "",
        name: input.name,
        origin: input.origin ?? "",
      };
    case "creature":
      return {
        ...baseElementData,
        creatureAbilities: input.creatureAbilities ?? "",
        creatureDescription: input.creatureDescription ?? "",
        name: input.name,
        speciesLoreEntryId: input.speciesLoreEntryId ?? null,
      };
    case "item-object":
      return {
        ...baseElementData,
        currentHolderEntityCardId: input.currentHolderEntityCardId ?? null,
        itemCategory: input.itemCategory ?? "artifact",
        itemDescription: input.itemDescription ?? "",
        name: input.name,
        origin: input.origin ?? "",
      };
  }
}

export async function getProjectEntityCapacity(
  projectId: string,
): Promise<ProjectEntityCapacitySummary> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_project_entity_capacity", {
    target_project_id: projectId,
  });

  if (error) {
    throw mapEntityError(error);
  }

  const capacityRow = data?.[0];
  if (!capacityRow) {
    throw new EntityActionError("not-found", "That project is unavailable.");
  }

  return mapEntityCapacityRow({
    entity_cards_limit: capacityRow.entity_cards_limit,
    entity_cards_remaining: capacityRow.entity_cards_remaining,
    entity_cards_used: capacityRow.entity_cards_used,
    is_unlimited: capacityRow.is_unlimited,
    tier: capacityRow.tier,
  });
}

export async function loadEntityWorkspace(projectId: string): Promise<EntityWorkspaceSnapshot> {
  ensureConfigured();

  const [cardsResult, relationshipsResult, loreResult, capacity] = await Promise.all([
    supabase.from("entity_cards").select("*").eq("project_id", projectId).order("updated_at", {
      ascending: false,
    }),
    supabase.from("relationships").select("*").eq("project_id", projectId),
    supabase
      .from("lore_entries")
      .select("id, title, type, canon_status")
      .eq("project_id", projectId),
    getProjectEntityCapacity(projectId),
  ]);

  if (cardsResult.error) {
    throw mapEntityError(cardsResult.error);
  }

  if (relationshipsResult.error) {
    throw mapEntityError(relationshipsResult.error);
  }

  if (loreResult.error) {
    throw mapEntityError(loreResult.error);
  }

  const cardRows = (cardsResult.data as EntityCardRow[] | null) ?? [];
  const imageUrlsByCardId = await resolveEntityImageUrls(cardRows);

  return buildEntityWorkspaceSnapshot({
    capacity,
    imageUrlsByCardId,
    loreEntries: ((loreResult.data as LoreLookupRow[] | null) ?? []).map((entry) => ({
      canon_status: entry.canon_status,
      id: entry.id,
      title: entry.title,
      type: entry.type,
    })),
    projectId,
    relationships: (relationshipsResult.data as EntityRelationshipRow[] | null) ?? [],
    rows: cardRows,
  });
}

export async function createEntityCard(
  projectId: string,
  input: Omit<CreateEntityCardInput, "projectId">,
): Promise<EntityCardRecord> {
  ensureConfigured();
  const validated = createEntityCardSchema.parse(input);
  const payload = buildEntityRecordPayload(
    validated,
  ) as unknown as Database["public"]["Tables"]["entity_cards"]["Insert"]["data"];

  const { data, error } = await supabase
    .from("entity_cards")
    .insert({
      data: payload,
      name: validated.name.trim(),
      project_id: projectId,
      type: toStoredEntityCardType(validated.type),
    })
    .select("*")
    .single();

  if (error) {
    throw mapEntityError(error);
  }

  let record = data as EntityCardRow;

  if (validated.imageFile) {
    try {
      const userId = await getCurrentUserId();
      const imagePath = await uploadEntityImage(userId, projectId, record.id, validated.imageFile);
      const updateResult = await supabase
        .from("entity_cards")
        .update({
          image_path: imagePath,
        })
        .eq("id", record.id)
        .select("*")
        .single();

      if (updateResult.error) {
        throw updateResult.error;
      }

      record = updateResult.data as EntityCardRow;
    } catch (uploadError) {
      await supabase.from("entity_cards").delete().eq("id", record.id);
      throw mapEntityError(uploadError);
    }
  }

  return record;
}

export async function updateEntityCard(
  cardId: string,
  input: UpdateEntityCardInput,
): Promise<EntityCardRecord> {
  ensureConfigured();
  const validated = updateEntityCardSchema.parse(input);
  const existingCard = await getOwnedEntityCardById(cardId);

  if (!existingCard) {
    throw new EntityActionError("not-found", "That Entity Card is unavailable.");
  }

  let nextImagePath = existingCard.image_path;
  let uploadedPath: string | null = null;

  if (validated.imageFile) {
    const userId = await getCurrentUserId();
    uploadedPath = await uploadEntityImage(
      userId,
      existingCard.project_id,
      existingCard.id,
      validated.imageFile,
    );
    nextImagePath = uploadedPath;
  } else if (validated.removeImage) {
    nextImagePath = null;
  }

  const payload = buildEntityRecordPayload(
    validated,
  ) as unknown as Database["public"]["Tables"]["entity_cards"]["Update"]["data"];

  const { data, error } = await supabase
    .from("entity_cards")
    .update({
      data: payload,
      image_path: nextImagePath,
      name: validated.name.trim(),
      type: toStoredEntityCardType(validated.type),
    })
    .eq("id", existingCard.id)
    .select("*")
    .single();

  if (error) {
    if (uploadedPath) {
      await deleteEntityImage(uploadedPath).catch(() => undefined);
    }
    throw mapEntityError(error);
  }

  if (
    existingCard.image_path
    && existingCard.image_path !== nextImagePath
    && (validated.removeImage || uploadedPath)
  ) {
    await deleteEntityImage(existingCard.image_path).catch(() => undefined);
  }

  return data as EntityCardRow;
}

export async function deleteEntityCard(cardId: string) {
  ensureConfigured();
  const existingCard = await getOwnedEntityCardById(cardId);

  if (!existingCard) {
    throw new EntityActionError("not-found", "That Entity Card is unavailable.");
  }

  const { error } = await supabase.from("entity_cards").delete().eq("id", cardId);

  if (error) {
    throw mapEntityError(error);
  }

  if (existingCard.image_path) {
    await deleteEntityImage(existingCard.image_path).catch(() => undefined);
  }

  return {
    deletedCardId: cardId,
  };
}

export async function createEntityRelationship(
  projectId: string,
  sourceEntityCardId: string,
  input: Omit<CreateEntityRelationshipInput, "projectId" | "sourceEntityCardId">,
): Promise<EntityRelationshipRecord> {
  ensureConfigured();
  const validated = entityRelationshipSchema.parse(input);

  const { data, error } = await supabase
    .from("relationships")
    .insert({
      from_id: sourceEntityCardId,
      from_kind: "entity",
      label: buildEntityRelationshipLabel(validated),
      project_id: projectId,
      rel_type: getStoredRelationshipType(validated.targetKind),
      to_id: validated.targetId,
      to_kind: toStoredRelationshipKind(validated.targetKind),
    })
    .select("*")
    .single();

  if (error) {
    throw mapEntityError(error);
  }

  return data as EntityRelationshipRow;
}

export async function updateEntityRelationship(
  relationshipId: string,
  input: UpdateEntityRelationshipInput,
): Promise<EntityRelationshipRecord> {
  ensureConfigured();
  const validated = entityRelationshipSchema.parse(input);

  const { data, error } = await supabase
    .from("relationships")
    .update({
      label: buildEntityRelationshipLabel(validated),
      rel_type: getStoredRelationshipType(validated.targetKind),
      to_id: validated.targetId,
      to_kind: toStoredRelationshipKind(validated.targetKind),
    })
    .eq("id", relationshipId)
    .select("*")
    .single();

  if (error) {
    throw mapEntityError(error);
  }

  return data as EntityRelationshipRow;
}

export async function deleteEntityRelationship(relationshipId: string) {
  ensureConfigured();

  const { error } = await supabase.from("relationships").delete().eq("id", relationshipId);

  if (error) {
    throw mapEntityError(error);
  }

  return {
    deletedRelationshipId: relationshipId,
  };
}

export async function searchRaceSpeciesOptions(
  projectId: string,
  searchText = "",
): Promise<EntityLoreOption[]> {
  ensureConfigured();

  let query = supabase
    .from("lore_entries")
    .select("id, title, type, canon_status")
    .eq("project_id", projectId)
    .eq("type", "race_species")
    .order("title", { ascending: true });

  const normalizedSearch = searchText.trim();
  if (normalizedSearch) {
    query = query.ilike("title", `%${normalizedSearch}%`);
  }

  const { data, error } = await query;
  if (error) {
    throw mapEntityError(error);
  }

  return (((data as LoreLookupRow[] | null) ?? []).map((entry) => ({
    canonStatus: entry.canon_status,
    canonStatusLabel: loreCanonStatusLabels[entry.canon_status],
    id: entry.id,
    title: entry.title,
    type: entry.type,
  })));
}

export async function searchCharacterTargets(
  projectId: string,
  searchText = "",
  excludeCardId?: string,
): Promise<EntityCardTargetOption[]> {
  ensureConfigured();

  let query = supabase
    .from("entity_cards")
    .select("id, name, type")
    .eq("project_id", projectId)
    .eq("type", "character")
    .order("name", { ascending: true });

  const normalizedSearch = searchText.trim();
  if (normalizedSearch) {
    query = query.ilike("name", `%${normalizedSearch}%`);
  }

  if (excludeCardId) {
    query = query.neq("id", excludeCardId);
  }

  const { data, error } = await query;
  if (error) {
    throw mapEntityError(error);
  }

  return (((data as Pick<EntityCardRow, "id" | "name" | "type">[] | null) ?? []).map((row) => ({
    description: "Character",
    id: row.id,
    name: row.name,
    type: "character",
  })));
}

export async function listRelationshipTargets(
  projectId: string,
): Promise<EntityRelationshipTargetOption[]> {
  return loadEntityWorkspace(projectId).then((workspace) => workspace.relationshipTargetOptions);
}
